import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Upload, Send, ShieldAlert, X, ChevronDown, ChevronUp, ZoomIn, Paperclip } from 'lucide-react';
import { getOrderDetail, uploadPaymentProof, markPaymentSent, releaseCrypto, cancelOrder, requestCancellation, respondCancellation } from '../../services/p2pService';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useP2P } from '../../context/P2PContext';

function useCountdown(expiresAt: string) {
  const [secs, setSecs] = useState(Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)));
  useEffect(() => {
    if (secs <= 0) return;
    const t = setInterval(() => setSecs(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);
  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');
  return { secs, formatted: `${mm}:${ss}`, expired: secs <= 0 };
}

function useCopy() {
  const [copied, setCopied] = useState('');
  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };
  return { copy, copied };
}

function ProofSheet({ order, onClose, onDone }: { order: any; onClose: () => void; onDone: () => void }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [images, setImages] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const maxImages = parseInt(import.meta.env.VITE_P2P_MAX_PROOF_IMAGES ?? '3');

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    files.forEach(f => {
      const reader = new FileReader();
      reader.onload = ev => {
        setImages(prev => prev.length < maxImages ? [...prev, ev.target?.result as string] : prev);
      };
      reader.readAsDataURL(f);
    });
  };

  const handleSubmit = async () => {
    if (images.length === 0) { toast('Upload at least one proof image', 'error'); return; }
    if (!confirmed) { toast('Confirm this is your own account', 'error'); return; }
    setLoading(true);
    try {
      if (images.length > 0) await uploadPaymentProof(order._id, images);
      await markPaymentSent(order._id, confirmed);
      toast('Payment confirmed!', 'success');
      onDone();
    } catch (e: any) { toast(e.message, 'error'); }
    setLoading(false);
  };

  return (
    <>
      <div className="p2p-overlay" onClick={onClose} />
      <div className="p2p-bottom-sheet">
        <div className="p2p-sheet-handle" />
        <div className="p2p-sheet-title">Upload Payment Proof</div>
        <div className="p2p-upload-grid" style={{ marginBottom: 16 }}>
          {images.map((img, i) => (
            <div key={i} className="p2p-upload-box" style={{ position: 'relative' }}>
              <img src={img} alt="proof" />
              <button onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))} className="p2p-upload-remove"><X size={12} color="#fff" /></button>
            </div>
          ))}
          {images.length < maxImages && (
            <label className="p2p-upload-box">
              <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFile} />
              <Upload size={24} color="var(--p2p-text-muted)" />
            </label>
          )}
        </div>
        <label className="p2p-checkbox-row" style={{ marginBottom: 20 }}>
          <input type="checkbox" className="p2p-checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
          <span style={{ fontSize: 13, color: '#333' }}>I confirm this payment was made from my own account ({user?.name})</span>
        </label>
        <button className="p2p-btn-yellow" style={{ color: '#000' }} disabled={loading || images.length === 0 || !confirmed} onClick={handleSubmit}>
          {loading ? 'Sending...' : 'Transferred, Notify Seller'}
        </button>
      </div>
    </>
  );
}

const CANCEL_REASONS = [
  'I made a mistake in the order details',
  'I no longer wish to complete this trade',
  'I cannot complete payment within the time limit',
  'Seller is unresponsive',
  'Agreed to cancel with counterparty',
  'Other',
];

function CancelModal({ order, onClose, onDone }: { order: any; onClose: () => void; onDone: () => void }) {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [loading, setLoading] = useState(false);
  const finalReason = reason === 'Other' ? customReason.trim() : reason;
  const handle = async () => {
    if (!finalReason) { toast('Please select a reason', 'error'); return; }
    setLoading(true);
    try { await cancelOrder(order._id, finalReason); toast('Order cancelled', 'success'); onDone(); }
    catch (e: any) { toast(e.message, 'error'); }
    setLoading(false);
  };
  return (
    <>
      <div className="p2p-overlay" onClick={onClose} />
      <div className="p2p-bottom-sheet">
        <div className="p2p-sheet-handle" />
        <div className="p2p-sheet-title">Cancel Order</div>
        <p style={{ color: 'var(--p2p-text-muted)', fontSize: 13, marginBottom: 14 }}>Select a reason for cancellation:</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {CANCEL_REASONS.map(r => (
            <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: `1px solid ${reason === r ? 'var(--p2p-buy)' : 'var(--p2p-border-default)'}`, background: reason === r ? 'rgba(28,212,167,0.07)' : 'var(--p2p-bg-tertiary)', cursor: 'pointer' }}>
              <input type="radio" name="cancel-reason" value={r} checked={reason === r} onChange={() => setReason(r)} style={{ accentColor: 'var(--p2p-buy)' }} />
              <span style={{ fontSize: 13, color: 'var(--p2p-text-primary)' }}>{r}</span>
            </label>
          ))}
        </div>
        {reason === 'Other' && (
          <textarea
            className="p2p-input"
            style={{ color: 'var(--p2p-text-primary)', background: 'var(--p2p-bg-tertiary)', borderColor: 'var(--p2p-border-default)', minHeight: 72, resize: 'none', marginBottom: 16 }}
            placeholder="Describe your reason..."
            value={customReason}
            onChange={e => setCustomReason(e.target.value)}
          />
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="p2p-btn-ghost" style={{ flex: 1 }} onClick={onClose}>Back</button>
          <button style={{ flex: 1, background: '#F6465D', color: '#fff', height: 48, border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: loading || !finalReason ? 0.6 : 1 }} disabled={loading || !finalReason} onClick={handle}>
            {loading ? '...' : 'Cancel Order'}
          </button>
        </div>
      </div>
    </>
  );
}

function CancelRequestModal({ order, onClose, onDone }: { order: any; onClose: () => void; onDone: () => void }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const cr = order.cancellationRequest;
  const isRequester = cr?.requestedBy === user?._id;
  
  const respond = async (accept: boolean) => {
    setLoading(true);
    try { await respondCancellation(order._id, accept); toast(accept ? 'Order cancelled' : 'Request rejected', 'success'); onDone(); }
    catch (e: any) { toast(e.message, 'error'); }
    setLoading(false);
  };

  return (
    <>
      <div className="p2p-overlay" onClick={onClose} />
      <div className="p2p-bottom-sheet">
        <div className="p2p-sheet-handle" />
        <div className="p2p-sheet-title">Cancellation Request</div>
        <div style={{ background: '#f4f4f4', borderRadius: 8, padding: 12, marginBottom: 16, color: '#555', fontSize: 14 }}>{cr?.reason}</div>
        {isRequester ? (
          <p style={{ color: '#555', fontSize: 14 }}>Waiting for the other party to respond.</p>
        ) : (
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="p2p-btn-ghost" style={{ flex: 1 }} disabled={loading} onClick={() => respond(false)}>Reject</button>
            <button className="p2p-btn-yellow" style={{ flex: 1 }} disabled={loading} onClick={() => respond(true)}>Accept</button>
          </div>
        )}
      </div>
    </>
  );
}

function Timer({ expiresAt }: { expiresAt: string }) {
  const { secs, formatted, expired } = useCountdown(expiresAt);
  return <div className={`p2p-timer ${secs < 300 ? 'urgent' : ''}`}>{expired ? 'EXPIRED' : formatted}</div>;
}

export default function ActiveOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { socket } = useP2P();
  const { copy, copied } = useCopy();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showProof, setShowProof] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showCancelReq, setShowCancelReq] = useState(false);
  const [msg, setMsg] = useState('');
  const [releasing, setReleasing] = useState(false);
  const [termsCollapsed, setTermsCollapsed] = useState(true);
  const [pmCollapsed, setPmCollapsed] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try { const o = await getOrderDetail(id!); setOrder(o); } catch { /* ignore */ }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!socket || !id) return;
    socket.emit('join:order', id);
    socket.on('order:message', ({ orderId, message }: any) => {
      if (orderId === id) setOrder((prev: any) => prev ? { ...prev, messages: [...prev.messages, message] } : prev);
    });
    socket.on('order:status', ({ orderId }: any) => { if (orderId === id) load(); });
    return () => { socket.off('order:message'); socket.off('order:status'); };
  }, [socket, id]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [order?.messages]);

  if (loading) return (
    <div style={{ padding: '16px' }}>
      {[...Array(3)].map((_, i) => (
        <div key={i} className="p2p-skel-card" style={{ flexDirection: 'column', gap: 10, marginBottom: 12, borderRadius: 8, border: '1px solid var(--p2p-border-default)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="p2p-skel" style={{ width: 120, height: 14 }} />
            <span className="p2p-skel" style={{ width: 60, height: 14, borderRadius: 4 }} />
          </div>
          <span className="p2p-skel" style={{ width: '60%', height: 12 }} />
          <span className="p2p-skel" style={{ width: '40%', height: 12 }} />
          <span className="p2p-skel" style={{ width: '100%', height: 36, borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );
  if (!order) return <div className="p2p-empty"><p className="p2p-empty-text">Order not found</p></div>;

  const isBuyer = order.buyer?._id === user?._id;
  const pm = order.paymentMethod;
  const isDone = ['completed','cancelled','expired'].includes(order.status);
  const counterparty = isBuyer ? order.seller : order.buyer;
  const counterpartyName = counterparty?.p2pProfile?.nickname || counterparty?.nickname || counterparty?.name || '—';

  const sendMsg = () => {
    if (!msg.trim() || !socket) return;
    socket.emit('send:message', { orderId: id, text: msg, type: 'text' });
    setMsg('');
  };

  const sendImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !socket) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const base64 = ev.target?.result as string;
      socket.emit('send:message', { orderId: id, type: 'image', imageBase64: base64, text: '' });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRelease = async () => {
    setReleasing(true);
    try { await releaseCrypto(order._id); toast('Crypto released!', 'success'); navigate(`/p2p/order/${order._id}/complete`); }
    catch (e: any) { toast(e.message, 'error'); }
    setReleasing(false);
  };

  return (
    <div className="p2p-page">
      {/* Header */}
      <div className="p2p-header">
        <button className="p2p-back-btn" onClick={() => navigate(-1)}><ArrowLeft size={20} />Order {order.orderNumber}</button>
        <button onClick={() => navigate(`/p2p/order/${order._id}/dispute`)} style={{ background: 'none', border: 'none', color: 'var(--p2p-sell)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <ShieldAlert size={16} />Appeal
        </button>
      </div>

      <div className="p2p-page-content">
        {/* Status / Timer */}
        <div style={{ padding: '20px 16px', textAlign: 'center', borderBottom: '1px solid var(--p2p-border-default)' }}>
          {!isDone && (
            <>
              <div style={{ fontSize: 14, color: 'var(--p2p-text-muted)', marginBottom: 4 }}>
                {isBuyer ? (order.status === 'pending_payment' ? 'Pay within' : 'Waiting for seller to release') : (order.status === 'pending_payment' ? 'Waiting for buyer payment' : 'Buyer marked payment sent')}
              </div>
              {order.status === 'pending_payment' && <Timer expiresAt={order.expiresAt} />}
            </>
          )}
          <div style={{ marginTop: 8 }}>
            <span className={`p2p-badge p2p-badge-${order.status === 'completed' ? 'completed' : order.status === 'cancelled' || order.status === 'expired' ? 'cancelled' : order.status === 'disputed' ? 'disputed' : 'pending'}`}>
              {order.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <div style={{ marginTop: 12, fontSize: 24, fontWeight: 700, color: isBuyer ? 'var(--p2p-buy)' : 'var(--p2p-sell)', fontVariantNumeric: 'tabular-nums' }}>
            {isBuyer ? '+' : '-'}{order.cryptoAmount?.toFixed(6)} {order.crypto}
          </div>
          <div style={{ fontSize: 13, color: 'var(--p2p-text-muted)' }}>{order.fiatAmount?.toLocaleString()} {order.fiat} @ {order.price?.toLocaleString()}</div>
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--p2p-text-muted)' }}>
            {isBuyer ? 'Seller' : 'Buyer'}: <span style={{ color: 'var(--p2p-text-secondary)', fontWeight: 600 }}>{counterpartyName}</span>
          </div>
        </div>

        {/* Escrow notice */}
        {order.status === 'pending_payment' && (
          <div className="p2p-escrow-notice">🔒 Seller's crypto is escrowed by Bicoin</div>
        )}

        {/* Payment Details */}
        {order.status === 'pending_payment' && (() => {
          const adPms = order.ad?.paymentMethods ?? [];
          const isSellAd = order.ad?.side === 'sell' || !order.ad?.side; // default SELL if not loaded

          if (isBuyer) {
            // Buyer: show seller's payment details + order instructions
            return (
              <div style={{ borderBottom: '1px solid var(--p2p-border-default)' }}>
                {/* Order amount reminder */}
                <div style={{ padding: '12px 16px', background: 'rgba(28,212,167,0.05)', borderBottom: '1px solid var(--p2p-border-default)' }}>
                  <div style={{ fontSize: 12, color: 'var(--p2p-text-muted)', marginBottom: 4 }}>Transfer this exact amount</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--p2p-text-primary)' }}>
                    {order.fiatAmount?.toLocaleString()} <span style={{ fontSize: 14, color: 'var(--p2p-text-muted)' }}>{order.fiat}</span>
                  </div>
                </div>

                {/* Seller payment details */}
                <div style={{ padding: '16px' }}>
                  <div style={{ fontSize: 13, color: 'var(--p2p-text-muted)', marginBottom: 12, fontWeight: 600 }}>📋 Send payment to seller via:</div>
                  {adPms.length > 0 ? adPms.map((adPm: any, idx: number) => (
                    <div key={idx} style={{ marginBottom: idx < adPms.length - 1 ? 12 : 0, padding: 12, background: 'var(--p2p-bg-tertiary)', borderRadius: 8 }}>
                      <div className="p2p-detail-row">
                        <span className="p2p-detail-label">Method</span>
                        <div className="p2p-detail-val-row">
                          <span className="p2p-pm-dot" style={{ background: adPm.methodColor ?? '#2563eb', width: 8, height: 8 }} />
                          <span className="p2p-detail-value">{adPm.methodType}</span>
                        </div>
                      </div>
                      {adPm.accountName && <div className="p2p-detail-row"><span className="p2p-detail-label">Account Name</span><div className="p2p-detail-val-row"><span className="p2p-detail-value">{adPm.accountName}</span><button className="p2p-copy-btn" onClick={() => copy(adPm.accountName, `name-${idx}`)}>{copied === `name-${idx}` ? <Check size={14}/> : <Copy size={14}/>}</button></div></div>}
                      {adPm.accountNumber && <div className="p2p-detail-row"><span className="p2p-detail-label">Account No.</span><div className="p2p-detail-val-row"><span className="p2p-detail-value">{adPm.accountNumber}</span><button className="p2p-copy-btn" onClick={() => copy(adPm.accountNumber, `num-${idx}`)}>{copied === `num-${idx}` ? <Check size={14}/> : <Copy size={14}/>}</button></div></div>}
                      {adPm.phoneNumber && <div className="p2p-detail-row"><span className="p2p-detail-label">Phone</span><div className="p2p-detail-val-row"><span className="p2p-detail-value">{adPm.phoneNumber}</span><button className="p2p-copy-btn" onClick={() => copy(adPm.phoneNumber, `phone-${idx}`)}>{copied === `phone-${idx}` ? <Check size={14}/> : <Copy size={14}/>}</button></div></div>}
                      {adPm.extraFields?.bankName && <div className="p2p-detail-row"><span className="p2p-detail-label">Bank</span><div className="p2p-detail-val-row"><span className="p2p-detail-value">{adPm.extraFields.bankName}</span></div></div>}
                    </div>
                  )) : (
                    <div style={{ padding: 12, background: 'var(--p2p-bg-tertiary)', borderRadius: 8, fontSize: 13, color: 'var(--p2p-text-muted)' }}>
                      Contact seller via chat for payment details
                    </div>
                  )}
                  <div style={{ marginTop: 10, fontSize: 12, color: 'var(--p2p-sell)', fontWeight: 500 }}>
                    ⚠ Do not include any notes or references in your payment
                  </div>
                </div>

                {/* Ad terms — collapsible */}
                {order.ad?.terms && (
                  <div style={{ padding: '0 16px 16px' }}>
                    <button
                      onClick={() => setTermsCollapsed(p => !p)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: termsCollapsed ? 0 : 6 }}
                    >
                      <span style={{ fontSize: 12, color: 'var(--p2p-text-muted)' }}>Seller's Terms</span>
                      {termsCollapsed ? <ChevronDown size={14} color='var(--p2p-text-muted)' /> : <ChevronUp size={14} color='var(--p2p-text-muted)' />}
                    </button>
                    {!termsCollapsed && (
                      <div style={{ fontSize: 12, color: 'var(--p2p-text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--p2p-bg-tertiary)', padding: 10, borderRadius: 8 }}>
                        {order.ad.terms}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          }

          if (!isBuyer) {
            // Seller: show order summary + wait instructions
            return (
              <div style={{ padding: '16px', borderBottom: '1px solid var(--p2p-border-default)' }}>
                <div style={{ fontSize: 13, color: 'var(--p2p-text-muted)', marginBottom: 12, fontWeight: 600 }}>📋 Order Summary</div>
                <div style={{ padding: 12, background: 'var(--p2p-bg-tertiary)', borderRadius: 8 }}>
                  <div className="p2p-detail-row">
                    <span className="p2p-detail-label">You send</span>
                    <span className="p2p-detail-value" style={{ color: 'var(--p2p-sell)', fontWeight: 700 }}>{parseFloat((order.cryptoAmount ?? 0).toFixed(8)).toString()} {order.crypto}</span>
                  </div>
                  <div className="p2p-detail-row">
                    <span className="p2p-detail-label">You receive</span>
                    <span className="p2p-detail-value" style={{ color: 'var(--p2p-buy)', fontWeight: 700 }}>{order.fiatAmount?.toLocaleString()} {order.fiat}</span>
                  </div>
                  <div className="p2p-detail-row">
                    <span className="p2p-detail-label">Rate</span>
                    <span className="p2p-detail-value">{order.price?.toLocaleString()} {order.fiat}</span>
                  </div>
                  {pm && <>
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--p2p-border-default)', fontSize: 12, color: 'var(--p2p-text-muted)', marginBottom: 6 }}>Buyer pays you via:</div>
                    <div className="p2p-detail-row"><span className="p2p-detail-label">Method</span><div className="p2p-detail-val-row"><span className="p2p-pm-dot" style={{ background: pm.methodColor ?? '#2563eb', width: 8, height: 8 }} /><span className="p2p-detail-value">{pm.methodType}</span></div></div>
                    {pm.accountName && <div className="p2p-detail-row"><span className="p2p-detail-label">Account</span><div className="p2p-detail-val-row"><span className="p2p-detail-value">{pm.accountName}</span></div></div>}
                    {pm.phoneNumber && <div className="p2p-detail-row"><span className="p2p-detail-label">Phone</span><div className="p2p-detail-val-row"><span className="p2p-detail-value">{pm.phoneNumber}</span></div></div>}
                    {pm.extraFields?.bankName && <div className="p2p-detail-row"><span className="p2p-detail-label">Bank</span><div className="p2p-detail-val-row"><span className="p2p-detail-value">{pm.extraFields.bankName}</span></div></div>}
                  </>}
                </div>
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--p2p-warning)', fontWeight: 500 }}>
                  ⏳ Wait for buyer to confirm payment, then release crypto
                </div>
              </div>
            );
          }

          return null;
        })()}

        {/* Chat */}
        <div style={{ padding: '0 0 8px' }}>
          <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--p2p-text-muted)', borderBottom: '1px solid var(--p2p-border-default)' }}>Order Chat</div>
          <div className="p2p-messages-area" style={{ maxHeight: 280 }}>
            {order.messages?.map((m: any, i: number) => {
              if (m.type === 'system') return <div key={i} className="p2p-msg-system">{m.text}</div>;
              const mine = m.sender?._id === user?._id;
              if (m.type === 'image' && m.imageUrl) {
                return (
                  <div key={i} className={mine ? 'p2p-msg-mine' : 'p2p-msg-theirs'}>
                    <div
                      onClick={() => setLightboxUrl(m.imageUrl)}
                      style={{ cursor: 'pointer', position: 'relative', width: 160, borderRadius: 10, overflow: 'hidden', border: '2px solid var(--p2p-border-default)' }}
                    >
                      <img
                        src={m.imageUrl}
                        alt="Payment proof"
                        style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                      />
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}
                        onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                        onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
                      >
                        <ZoomIn size={28} color="#fff" />
                      </div>
                      <div style={{ padding: '4px 8px', background: mine ? 'var(--p2p-buy)' : 'var(--p2p-bg-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ fontSize: 11, color: mine ? '#000' : 'var(--p2p-text-muted)', fontWeight: 600 }}>📎 Payment proof</span>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div key={i} className={mine ? 'p2p-msg-mine' : 'p2p-msg-theirs'}>
                  <div className={mine ? 'p2p-bubble-mine' : 'p2p-bubble-theirs'}>{m.text}</div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
          {!isDone && (
            <div className="p2p-chat-input-row">
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--p2p-bg-tertiary)', border: '1px solid var(--p2p-border-default)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              >
                <Paperclip size={17} color="var(--p2p-text-muted)" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={sendImage} />
              <input className="p2p-chat-input" placeholder="Type a message..." value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMsg()} />
              <button className="p2p-chat-send" onClick={sendMsg}><Send size={16} color="#000" /></button>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {!isDone && (
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--p2p-border-default)', background: 'var(--p2p-bg-secondary)' }}>
          {isBuyer && order.status === 'pending_payment' && (
            <>
              <button className="p2p-btn-yellow" onClick={() => setShowProof(true)}>Upload Payment Proof</button>
              {order.paymentProofImages?.length > 0 && (
                <button className="p2p-btn-yellow" onClick={() => { uploadPaymentProof(order._id, []); markPaymentSent(order._id, true).then(load); }}>
                  Transferred, Notify Seller
                </button>
              )}
              <button className="p2p-btn-ghost" onClick={() => setShowCancel(true)}>Cancel Order</button>
            </>
          )}
          {!isBuyer && order.status === 'payment_sent' && (
            <button className="p2p-btn-yellow" style={{ background: 'var(--p2p-buy)', color: '#000' }} disabled={releasing} onClick={handleRelease}>
              {releasing ? 'Releasing...' : 'Payment Received, Release Crypto'}
            </button>
          )}
          {order.cancellationRequest?.requestedBy && order.cancellationRequest?.status === 'pending' && (
            <button className="p2p-btn-ghost" onClick={() => setShowCancelReq(true)}>View Cancellation Request</button>
          )}
        </div>
      )}
      {order.status === 'completed' && (
        <div style={{ padding: 16 }}>
          <button className="p2p-btn-yellow" onClick={() => navigate(`/p2p/order/${order._id}/complete`)}>View Completion</button>
        </div>
      )}

      {showProof && <ProofSheet order={order} onClose={() => setShowProof(false)} onDone={() => { setShowProof(false); load(); }} />}
      {showCancel && <CancelModal order={order} onClose={() => setShowCancel(false)} onDone={() => { setShowCancel(false); load(); }} />}
      {showCancelReq && <CancelRequestModal order={order} onClose={() => setShowCancelReq(false)} onDone={() => { setShowCancelReq(false); load(); }} />}

      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 38, height: 38, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={18} color="#fff" />
          </button>
          <img
            src={lightboxUrl}
            alt="Payment proof"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '92vw', maxHeight: '88vh', borderRadius: 10, objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
          />
        </div>
      )}
    </div>
  );
}
